import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Empty,
  Field,
  Note,
  Panel,
  Spinner,
  TableWrap,
} from '@wyocspay/ui';
import { dateTime, relative } from './format.js';
import {
  ApiError,
  api,
  type TicketAttachmentMeta as TicketAttachment,
  type TicketMessage,
  type TicketSummary,
  type TicketThread,
} from './api.js';

/**
 * Support tickets, merchant side.
 *
 * The merchant opens a thread, attaches evidence, and reads replies. Internal
 * notes never reach this panel - they are filtered on the server, so nothing
 * here needs to remember to hide them.
 */

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 5;

const CATEGORIES = [
  ['general', 'General question'],
  ['payments', 'A payment'],
  ['payouts', 'Payouts and settlement'],
  ['disputes', 'A dispute or chargeback'],
  ['technical', 'API or technical'],
  ['account', 'My account'],
  ['billing', 'Fees and billing'],
] as const;

const PRIORITIES = [
  ['low', 'Low'],
  ['normal', 'Normal'],
  ['high', 'High'],
  ['urgent', 'Urgent - money is stuck'],
] as const;

// ---------------------------------------------------------------------------

const STATUS_TONE: Record<string, 'ok' | 'pending' | 'warn' | 'neutral' | 'info'> = {
  OPEN: 'warn',
  AWAITING_ADMIN: 'warn',
  AWAITING_MERCHANT: 'info',
  RESOLVED: 'ok',
  CLOSED: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  AWAITING_ADMIN: 'With support',
  AWAITING_MERCHANT: 'Needs your reply',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

export function statusBadge(status: string) {
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Read a File into base64 for the JSON body.
 *
 * FileReader gives a data URL; only the part after the comma is the payload.
 * Multipart would avoid the ~33% encoding overhead, but at a 5 MB cap that is
 * not worth a second body parser on an API that is otherwise entirely JSON.
 */
export function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------

export function AttachmentPicker({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (next: File[]) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  function add(list: FileList | null): void {
    if (!list) return;
    const incoming = Array.from(list);
    const tooBig = incoming.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      setError(`${tooBig.name} is larger than 5 MB.`);
      return;
    }
    if (files.length + incoming.length > MAX_FILES) {
      setError(`Up to ${MAX_FILES} files per message.`);
      return;
    }
    setError(null);
    onChange([...files, ...incoming]);
  }

  return (
    <div>
      <input
        ref={input}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
        style={{ display: 'none' }}
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="wc-btn wc-btn-sm wc-btn-secondary"
          onClick={() => input.current?.click()}
          disabled={disabled || files.length >= MAX_FILES}
        >
          Attach a file
        </button>
        <span style={{ fontSize: 12, color: 'var(--wc-faint)' }}>
          Images or PDF, up to 5 MB each
        </span>
      </div>

      {files.length > 0 ? (
        <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 4 }}>
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
            >
              <span style={{ flex: 1, wordBreak: 'break-all' }}>{f.name}</span>
              <span style={{ color: 'var(--wc-faint)', fontSize: 12 }}>{formatBytes(f.size)}</span>
              <button
                type="button"
                className="wc-btn wc-btn-sm wc-btn-secondary"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                disabled={disabled}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {error ? <div style={{ marginTop: 8 }}><Note tone="warn">{error}</Note></div> : null}
    </div>
  );
}

/**
 * Attachment links.
 *
 * The bytes come from an authenticated endpoint, so a plain <a href> would
 * arrive without the bearer token. The file is fetched, turned into a blob URL
 * and handed to the browser, and the URL is revoked once it has been used.
 */
export function AttachmentLinks({
  attachments,
  fetchUrl,
}: {
  attachments: TicketAttachment[];
  fetchUrl: (id: string) => Promise<Blob>;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function openFile(a: TicketAttachment): Promise<void> {
    setBusy(a.id);
    try {
      const blob = await fetchUrl(a.id);
      const url = URL.createObjectURL(blob);
      if (a.inline) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = a.filename;
        link.click();
      }
      // Long enough for the browser to have taken the bytes.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setBusy(null);
    }
  }

  if (attachments.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
      {attachments.map((a) => (
        <button
          key={a.id}
          type="button"
          className="wc-btn wc-btn-sm wc-btn-secondary"
          onClick={() => void openFile(a)}
          disabled={busy === a.id}
          title={`${a.filename} · ${formatBytes(a.size)}`}
        >
          {busy === a.id ? 'Opening…' : a.filename}
          <span style={{ color: 'var(--wc-faint)', marginLeft: 6, fontSize: 11 }}>
            {formatBytes(a.size)}
          </span>
        </button>
      ))}
    </div>
  );
}

export function MessageList({
  messages,
  fetchAttachment,
}: {
  messages: TicketMessage[];
  fetchAttachment: (id: string) => Promise<Blob>;
}) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {messages.map((m) => {
        const system = m.author_side === 'SYSTEM';
        const mine = m.author_side === 'MERCHANT';
        if (system) {
          return (
            <div
              key={m.id}
              style={{
                fontSize: 12.5,
                color: 'var(--wc-muted)',
                textAlign: 'center',
                padding: '4px 0',
              }}
            >
              {m.body} · {relative(m.created_at)}
            </div>
          );
        }
        return (
          <div
            key={m.id}
            style={{
              border: '1px solid var(--wc-border)',
              borderLeft: `3px solid ${
                m.internal ? 'var(--wc-amber)' : mine ? 'var(--wc-blue)' : 'var(--wc-green)'
              }`,
              background: m.internal ? 'var(--wc-amber-tint)' : 'var(--wc-surface)',
              padding: '10px 14px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 10,
                alignItems: 'baseline',
                marginBottom: 6,
              }}
            >
              <strong style={{ fontSize: 13.5 }}>
                {m.author_name}
                {m.internal ? (
                  <Badge tone="warn" icon={false}>
                    INTERNAL
                  </Badge>
                ) : null}
              </strong>
              <span style={{ fontSize: 12, color: 'var(--wc-faint)' }} title={dateTime(m.created_at)}>
                {relative(m.created_at)}
              </span>
            </div>
            <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {m.body}
            </div>
            <AttachmentLinks attachments={m.attachments} fetchUrl={fetchAttachment} />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function TicketsView() {
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('all');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.tickets(status);
      setTickets(res.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  if (openId) {
    return (
      <TicketThreadView
        id={openId}
        onBack={() => {
          setOpenId(null);
          void load();
        }}
      />
    );
  }

  if (creating) {
    return (
      <NewTicketView
        onCancel={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          setOpenId(id);
        }}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <Note>
        Anything about a payment, a payout, a dispute or your account. Attach screenshots or
        documents and the whole thread stays on the ticket, with a reference number you can quote.
      </Note>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="wc-btn" onClick={() => setCreating(true)}>
          New ticket
        </button>
        <div style={{ flex: 1 }} />
        {['all', 'OPEN', 'AWAITING_MERCHANT', 'RESOLVED', 'CLOSED'].map((s) => (
          <button
            key={s}
            type="button"
            className={`wc-btn wc-btn-sm ${status === s ? '' : 'wc-btn-secondary'}`}
            onClick={() => setStatus(s)}
          >
            {s === 'all' ? 'All' : (STATUS_LABEL[s] ?? s)}
          </button>
        ))}
      </div>

      {error ? <Note tone="danger">{error}</Note> : null}

      {tickets === null ? (
        <Spinner label="Loading…" />
      ) : tickets.length === 0 ? (
        <Panel padded={false}>
          <Empty>No tickets yet.</Empty>
        </Panel>
      ) : (
        <Panel padded={false}>
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Messages</th>
                  <th>Last activity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id}>
                    <td className="wc-num" style={{ fontSize: 12.5 }}>
                      {t.number}
                    </td>
                    <td>
                      <strong style={{ fontSize: 14 }}>{t.subject}</strong>
                      <div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>
                        {t.category} · {t.priority}
                      </div>
                    </td>
                    <td>{statusBadge(t.status)}</td>
                    <td className="wc-num">{t.messages}</td>
                    <td style={{ fontSize: 13 }} title={dateTime(t.last_message_at)}>
                      {relative(t.last_message_at)}
                    </td>
                    <td className="wc-right">
                      <button
                        type="button"
                        className="wc-btn wc-btn-sm wc-btn-secondary"
                        onClick={() => setOpenId(t.id)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Panel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function NewTicketView({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({ filename: f.name, content: await toBase64(f) })),
      );
      const created = await api.createTicket({ subject, body, category, priority, attachments });
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 720 }}>
      <button
        type="button"
        className="wc-btn wc-btn-sm wc-btn-secondary"
        onClick={onCancel}
        style={{ justifySelf: 'start' }}
      >
        ← Back to tickets
      </button>

      <Panel title="New ticket">
        <Field label="Subject" hint="One line describing the problem">
          <input
            className="wc-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="What is it about">
            <select
              className="wc-input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select
              className="wc-input"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITIES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Details" hint="Payment ids, amounts and dates make this much faster to answer">
          <textarea
            className="wc-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            maxLength={20_000}
            style={{ resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>

        <div style={{ marginBottom: 14 }}>
          <AttachmentPicker files={files} onChange={setFiles} disabled={busy} />
        </div>

        {error ? <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div> : null}

        <button
          type="button"
          className="wc-btn"
          onClick={submit}
          disabled={busy || subject.trim().length < 3 || body.trim().length < 3}
        >
          {busy ? 'Sending…' : 'Open ticket'}
        </button>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------

function TicketThreadView({ id, onBack }: { id: string; onBack: () => void }) {
  const [thread, setThread] = useState<TicketThread | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setThread(await api.ticket(id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function send(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({ filename: f.name, content: await toBase64(f) })),
      );
      await api.replyToTicket(id, { body, attachments });
      setBody('');
      setFiles([]);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(status: 'RESOLVED' | 'OPEN'): Promise<void> {
    setBusy(true);
    try {
      await api.setTicketStatus(id, status);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !thread) return <Note tone="danger">{error}</Note>;
  if (!thread) return <Spinner label="Loading…" />;

  const closed = thread.status === 'CLOSED';

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 860 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={onBack}>
          ← Back to tickets
        </button>
        <div style={{ flex: 1 }} />
        {!closed && thread.status !== 'RESOLVED' ? (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            onClick={() => void setStatus('RESOLVED')}
            disabled={busy}
          >
            This is resolved
          </button>
        ) : null}
        {!closed && thread.status === 'RESOLVED' ? (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            onClick={() => void setStatus('OPEN')}
            disabled={busy}
          >
            Reopen
          </button>
        ) : null}
      </div>

      <Panel>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span className="wc-num" style={{ fontSize: 13 }}>
            {thread.number}
          </span>
          <strong style={{ fontSize: 16, flex: 1 }}>{thread.subject}</strong>
          {statusBadge(thread.status)}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--wc-muted)', marginTop: 4 }}>
          {thread.category} · {thread.priority} · opened {dateTime(thread.created_at)}
        </div>
      </Panel>

      <MessageList messages={thread.messages} fetchAttachment={api.ticketAttachment} />

      {closed ? (
        <Note>
          This ticket is closed. Open a new one and quote {thread.number} if you need to come back
          to it.
        </Note>
      ) : (
        <Panel title="Reply">
          <textarea
            className="wc-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            maxLength={20_000}
            placeholder="Your reply"
            style={{ resize: 'vertical', fontFamily: 'inherit', marginBottom: 12 }}
          />
          <div style={{ marginBottom: 12 }}>
            <AttachmentPicker files={files} onChange={setFiles} disabled={busy} />
          </div>
          {error ? <div style={{ marginBottom: 12 }}><Note tone="danger">{error}</Note></div> : null}
          <button
            type="button"
            className="wc-btn"
            onClick={send}
            disabled={busy || body.trim().length === 0}
          >
            {busy ? 'Sending…' : 'Send reply'}
          </button>
        </Panel>
      )}
    </div>
  );
}
